/**
 * Mover card de etapa: otimismo, e a volta quando o servidor recusa (H09).
 *
 * ── Por que otimismo AQUI, e não no envio de mensagem ─────────────────────
 *
 * A H06 proíbe otimismo antes da aceitação; esta task o permite, e a Técnica
 * explica a diferença: *"mover card é reversível e o feedback imediato importa
 * muito para arrastar e soltar. A reversão em 409 é o que torna aceitável."*
 *
 * Arrastar e ver o card parado até o servidor responder quebra a manipulação
 * direta — a mão já soltou, e a tela não acompanhou. Mensagem enviada é
 * diferente: não dá para desenviar.
 *
 * ── A armadilha é não tratar o 409 ───────────────────────────────────────
 *
 * *"O card fica no destino, o servidor manteve a etapa antiga, e a tela mostra
 * uma realidade que não existe até alguém recarregar."*
 *
 * É a pior combinação possível, porque o vendedor age sobre o que vê: liga para
 * o cliente achando que a proposta foi enviada, e ela não foi. A recuperação
 * tem três partes, e as três importam: o card volta, o motivo aparece, e o
 * estado é recarregado.
 *
 * ── O que a tela confere ANTES de tentar, e o que ela não confere ─────────
 *
 * Confere o que o contrato afirma: coluna terminal não deixa arrastar para fora
 * (`aberta: false`), e mover para a etapa em que já se está não é movimento.
 *
 * NÃO confere a regra de transição — pulo de etapa, saída de etapa fechada. Ela
 * é do produto e vive na D04, e a task é explícita em deixá-la fora daqui.
 * Reimplementá-la no navegador criaria duas versões da mesma regra, e a do
 * cliente é a que ninguém atualiza quando a revenda muda o funil.
 */

import { ErroDaApi, ErroDeRede } from '@cliqcrm/contracts';

export interface CardParaMover {
  readonly id: string | null;
  readonly etapa: string;
  readonly version: string;
}

export interface ColunaDestino {
  readonly etapa: string;
  readonly aberta: boolean;
}

export type Recusa = { readonly pode: true } | { readonly pode: false; readonly porque: string };

/**
 * Dá para tentar mover este card para esta coluna?
 *
 * Devolve o motivo junto, e não só o booleano: a tela precisa dizer por que o
 * card não entrou, e um `false` obrigaria cada chamador a inventar a frase.
 */
export function podeTentarMover(
  card: CardParaMover,
  origem: ColunaDestino,
  destino: ColunaDestino,
): Recusa {
  if (card.id === null) {
    // Sem canônico não há o que mandar no `PATCH`: a C13 só cunha quando a
    // oportunidade passa pela nossa API. Dizer isso é melhor que um card que
    // não se move sem explicação.
    return {
      pode: false,
      porque: 'Esta oportunidade foi criada fora do CliqCRM e ainda não pode ser movida por aqui.',
    };
  }

  if (destino.etapa === card.etapa) return { pode: false, porque: 'O card já está nesta etapa.' };

  if (!origem.aberta) {
    return {
      pode: false,
      porque: 'Esta etapa é final. A oportunidade não volta arrastando.',
    };
  }

  return { pode: true };
}

export interface DiagnosticoDoMovimento {
  readonly motivo: string;
  readonly acao: string;
  /** `true` = o estado na tela não vale mais; rebuscar antes de tentar de novo. */
  readonly recarregar: boolean;
  readonly correlationId: string | null;
}

/**
 * O que dizer quando o servidor recusa o movimento.
 *
 * Cada código pede uma frase diferente, e a diferença não é estética: o 409
 * significa que a tela está velha e insistir com a mesma versão daria o mesmo
 * erro; o 422 de transição significa que o movimento é proibido e recarregar
 * não muda nada.
 */
export function diagnosticarMovimento(erro: unknown): DiagnosticoDoMovimento {
  if (erro instanceof ErroDeRede) {
    return {
      motivo: 'Não foi possível falar com o servidor.',
      acao: 'O card voltou para onde estava. Confira a conexão e tente de novo.',
      recarregar: false,
      correlationId: null,
    };
  }

  if (!(erro instanceof ErroDaApi)) {
    return {
      motivo: 'Algo deu errado antes de o movimento sair.',
      acao: 'O card voltou para onde estava. Recarregue o funil.',
      recarregar: true,
      correlationId: null,
    };
  }

  const acao = ACAO_POR_CODIGO[erro.codigo];

  return {
    motivo: erro.problema.detail ?? erro.problema.title,
    acao: acao ?? 'O card voltou para onde estava.',
    // O 409 é o caso que a task nomeia, e a única resposta correta é recarregar:
    // a versão que a tela tem já não existe.
    recarregar: erro.codigo === 'CONFLITO_DE_VERSAO' || erro.codigo === 'VERSAO_EXIGIDA',
    // Recusa de regra já se explica sozinha: o código de suporte ali só assusta.
    correlationId: RECUSAS_DE_REGRA.has(erro.codigo) ? null : erro.correlationId,
  };
}

const RECUSAS_DE_REGRA = new Set(['REQUISICAO_INVALIDA', 'SEM_PERMISSAO', 'NAO_ENCONTRADO']);

/**
 * O mesmo diagnóstico, para CRIAR oportunidade ou tarefa pela tela do funil.
 *
 * Ali não há card voltando para lugar nenhum: dizer "o card voltou para onde
 * estava" descreveria um movimento que não houve. E recusa ao criar costuma
 * ser pedido malformado, que é defeito — o código de suporte fica, porque é
 * ele que leva alguém até o log.
 */
export function diagnosticarCriacao(erro: unknown): DiagnosticoDoMovimento {
  if (erro instanceof ErroDeRede) {
    return {
      motivo: 'Não foi possível falar com o servidor.',
      acao: 'Nada foi criado. Confira a conexão e tente de novo.',
      recarregar: false,
      correlationId: null,
    };
  }
  if (!(erro instanceof ErroDaApi)) {
    return {
      motivo: 'Algo deu errado antes de o pedido sair.',
      acao: 'Nada foi criado. Recarregue o funil.',
      recarregar: true,
      correlationId: null,
    };
  }
  return {
    motivo: erro.problema.detail ?? erro.problema.title,
    acao:
      erro.codigo === 'ENGINE_INDISPONIVEL'
        ? 'Não foi possível criar agora. Nada foi criado, tente em alguns minutos.'
        : 'Nada foi criado.',
    recarregar: false,
    correlationId: erro.correlationId,
  };
}

const ACAO_POR_CODIGO: Record<string, string> = {
  CONFLITO_DE_VERSAO:
    'Outra pessoa moveu este card antes de você. Ele voltou ao lugar e o funil foi atualizado — confira e mova de novo se ainda fizer sentido.',
  VERSAO_EXIGIDA: 'O funil foi recarregado. Tente mover de novo.',
  REQUISICAO_INVALIDA: 'O card voltou para onde estava.',
  SEM_PERMISSAO: 'Seu papel não permite mover este card. Fale com o administrador da organização.',
  ENGINE_INDISPONIVEL:
    'Não foi possível mover agora. O card voltou ao lugar e nada foi alterado, tente em alguns minutos.',
  NAO_ENCONTRADO: 'Esta oportunidade não está mais disponível para você. O funil foi recarregado.',
};

/**
 * Os movimentos otimistas em curso, por card.
 *
 * Um mapa e não um único valor: o gestor pode arrastar dois cards em sequência
 * antes de a primeira resposta chegar, e um valor só faria o segundo apagar o
 * primeiro — o primeiro card voltaria sozinho para a coluna antiga, sem erro
 * nenhum, o que é exatamente o tipo de fantasma difícil de reproduzir.
 */
export interface MovimentoEmCurso {
  readonly destino: string;
  /** O servidor já respondeu que sim. Daí em diante isto não é palpite. */
  readonly confirmado: boolean;
  /** Quando entrou aqui — só para parar de reperguntar ao servidor. */
  readonly desde: number;
}

export type MovimentosEmCurso = ReadonlyMap<string, MovimentoEmCurso>;

export function comMovimento(
  atuais: MovimentosEmCurso,
  cardId: string,
  etapaDestino: string,
  agora = Date.now(),
): MovimentosEmCurso {
  const novo = new Map(atuais);
  novo.set(cardId, { destino: etapaDestino, confirmado: false, desde: agora });
  return novo;
}

/**
 * O servidor aceitou. O card FICA no destino até o board concordar.
 *
 * ── A corrida que isto fecha ──────────────────────────────────────────────
 *
 * Antes, o sucesso apagava o movimento na hora e mandava rebuscar. Só que o
 * board lê a PROJEÇÃO, e ela é escrita por outro caminho — o webhook do Twenty.
 *
 * Medido no staging em 10/09: a projeção chega **33 ms** depois da escrita. Não
 * é lentidão, é empate. A rebusca sai junto com a projeção e às vezes chega
 * antes: quando chega antes, ela traz a etapa ANTIGA e o card volta sozinho
 * para a coluna de onde saiu — depois de o servidor ter aceitado.
 *
 * Daí os dois sintomas, que são o mesmo: às vezes vai, às vezes volta. O
 * registro de auditoria do staging mostra o mesmo card movido três vezes em
 * quatro segundos, 12:58:30, :32 e :34 — alguém tentando de novo porque a tela
 * desfazia o que já tinha dado certo.
 *
 * Esperar o board concordar tira a corrida dos dois lados: não importa quem
 * chega primeiro.
 *
 * ── Por que não some por tempo ────────────────────────────────────────────
 *
 * Depois do sucesso, o card no destino não é otimismo — é o que de fato
 * aconteceu. Apagá-lo por tempo mostraria algo falso, que é o defeito de novo,
 * só que mais devagar. O que o tempo limita é reperguntar, não o que se vê.
 */
export function comMovimentoConfirmado(
  atuais: MovimentosEmCurso,
  cardId: string,
): MovimentosEmCurso {
  const emCurso = atuais.get(cardId);
  if (!emCurso) return atuais;
  const novo = new Map(atuais);
  novo.set(cardId, { ...emCurso, confirmado: true });
  return novo;
}

/**
 * Larga os movimentos que o board já mostra sozinho — e os cards que sumiram.
 *
 * Sumiu é o filtro em vigor deixando o card de fora, ou ele saiu do escopo.
 * Segurar um movimento de card que ninguém desenha só faria a tela reperguntar
 * ao servidor para sempre.
 */
export function conciliar(
  atuais: MovimentosEmCurso,
  cardsDoBoard: readonly { readonly id: string | null; readonly etapa: string }[],
): MovimentosEmCurso {
  const porId = new Map(cardsDoBoard.flatMap((c) => (c.id ? [[c.id, c.etapa] as const] : [])));
  const novo = new Map(atuais);
  for (const [cardId, emCurso] of atuais) {
    const noBoard = porId.get(cardId);
    if (noBoard === undefined || noBoard === emCurso.destino) novo.delete(cardId);
  }
  return novo.size === atuais.size ? atuais : novo;
}

/**
 * Ainda vale reperguntar ao servidor por algum destes movimentos?
 *
 * Só os confirmados: um movimento sem resposta ainda tem a própria resposta a
 * caminho, e rebuscar por causa dele seria pedir duas vezes a mesma coisa.
 */
export function esperandoOBoard(atuais: MovimentosEmCurso, agora = Date.now()): boolean {
  for (const emCurso of atuais.values()) {
    if (emCurso.confirmado && agora - emCurso.desde < TETO_DA_ESPERA_MS) return true;
  }
  return false;
}

/**
 * Dez segundos. A projeção mede 33 ms; isto é o que sobra para uma fila presa.
 * Passado disso, o card continua onde está — o que para é a repergunta.
 */
const TETO_DA_ESPERA_MS = 10_000;

export function semMovimento(atuais: MovimentosEmCurso, cardId: string): MovimentosEmCurso {
  const novo = new Map(atuais);
  novo.delete(cardId);
  return novo;
}

/**
 * A etapa em que o card aparece agora — a otimista, se houver.
 *
 * Aplicada na leitura, e não mutando o board: o board é cache do servidor, e
 * escrever nele faria a rebusca seguinte parecer um retrocesso.
 */
export function etapaVisivel(
  card: { readonly id: string | null; readonly etapa: string },
  emCurso: MovimentosEmCurso,
): string {
  return (card.id && emCurso.get(card.id)?.destino) || card.etapa;
}

/**
 * As versões que o servidor devolveu, por card.
 *
 * ── A corrida que isto fecha ──────────────────────────────────────────────
 *
 * O board lê a versão da PROJEÇÃO, que só é atualizada quando o webhook do
 * Twenty chega. Mover um card muda a versão na hora, lá; a projeção leva
 * alguns instantes.
 *
 * Nesse intervalo, invalidar e rebuscar devolve a versão ANTIGA — e o segundo
 * arrasto do mesmo card é recusado com 409, dizendo que alguém o mudou. Foi o
 * que aconteceu em 01/09: cada arrasto funcionava, o seguinte falhava, e a
 * mensagem culpava uma concorrência que não existia.
 *
 * A resposta do `PATCH` já traz a versão nova. Guardá-la aqui é o que permite
 * arrastar duas vezes seguidas sem esperar a projeção alcançar.
 *
 * ── Por que na leitura, e não escrevendo no cache ─────────────────────────
 *
 * Mesmo motivo de `etapaVisivel`: o board é cache do servidor, e escrever nele
 * faria a rebusca seguinte parecer um retrocesso. Quando a projeção alcança,
 * ela traz a mesma versão — e o registro daqui deixa de importar sozinho.
 */
export type VersoesFrescas = ReadonlyMap<string, string>;

export function comVersaoFresca(
  atuais: VersoesFrescas,
  cardId: string,
  version: string,
): VersoesFrescas {
  const novo = new Map(atuais);
  novo.set(cardId, version);
  return novo;
}

/**
 * A versão a mandar no próximo movimento — a fresca, se houver.
 *
 * Sem `id` não há o que guardar nem o que buscar: card que ainda não tem
 * canônico não foi movido por ninguém.
 */
export function versaoParaEnviar(
  card: { readonly id: string | null; readonly version: string },
  frescas: VersoesFrescas,
): string {
  return (card.id && frescas.get(card.id)) || card.version;
}
